<template>
  <div class="flex flex-col">
    <div
      class="radiator-card border border-gray-300 dark:border-dark-3 bg-neutral-600 dark:bg-dark-1"
      data-testid="radiatorCard"
    >
      <div class="radiator-card-header text-white dark:text-white">
        <div>
          <em :class="['mdi', { 'mdi-check-circle mr-2': isIdentified }]" />
          <em v-if="showRadiatorHasImage" class="mdi mdi-camera" />
        </div>

        <div class="relative flex justify-between align-middle">
          <div class="text-2xl font-bold">
            {{ radiator.getRadiatorLabel() }}
          </div>

          <OptionsDropdown
            class="text-black dark:text-white"
            icon="mdi-dots-vertical"
            direction-x="left"
            direction="down"
            :data-testid="`radiatorDropdownButton_${radiator.radiatorType}`"
          >
            <li
              :data-testid="`editDropdownItem_${radiator.radiatorType}`"
              :class="[
                {
                  'cursor-not-allowed text-light-disabled-text hover:bg-transparent dark:text-dark-disabled-text':
                    !showEdit,
                },
              ]"
              @click="
                showEdit
                  ? goToEditRadiator(radiator)
                  : showDisabledNotification()
              "
            >
              <em class="mdi mdi-pencil mr-2" />Bearbeiten
            </li>
            <li
              :data-testid="`deleteDropdownItem_${radiator.radiatorType}`"
              :class="[
                {
                  'cursor-not-allowed text-light-disabled-text hover:bg-transparent dark:text-dark-disabled-text':
                    !showDelete,
                },
              ]"
              @click="
                showDelete
                  ? deleteEntityModal(radiator)
                  : showDisabledNotification()
              "
            >
              <em class="mdi mdi-delete mr-2" />Löschen
            </li>
          </OptionsDropdown>
        </div>

        <div class="is-size-6">
          {{ isIdentified ? radiator.identificationResult?.model : '-' }}
        </div>

        <div class="is-size-6 mt-1">{{ radiator.getDimensions() }}</div>
      </div>

      <div
        class="radiator-card-body flex flex-col gap-4 bg-neutral-100 dark:bg-dark-3"
      >
        <RadiatorCardDeviceList
          :device-type="deviceTypeDetails[DeviceTypeEnum.HEAT_COST_ALLOCATOR]"
          :devices="getHKVsOfRadiator"
          :radiator="radiator"
          :parent-id="parentId"
        />
        <RadiatorCardDeviceList
          :device-type="
            deviceTypeDetails[DeviceTypeEnum.SMART_RADIATOR_THERMOSTAT]
          "
          :devices="getSRTsOfRadiator"
          :parent-id="parentId"
          :radiator="radiator"
        />
      </div>
    </div>
    <em
      v-if="!isLastRadiator && isSwapModeActive"
      class="mdi mdi-swap-horizontal swap-btn left mt-6 justify-center"
      data-testid="swapButton"
      @click="swapRadiator(radiator)"
    />
    <BasePrompt
      :open="isDeleteActive"
      :close="closeModal"
      :cancel="closeModal"
      :proceed="deleteRadiator"
      title="Heizkörper löschen"
      question="Bist du sicher, dass du diesen Heizkörper löschen möchtest?"
    >
    </BasePrompt>
  </div>
</template>

<script lang="ts">
import { deviceTypeDetails, DeviceTypeEnum, deviceTypes } from '@/enums/device';
import { Radiator } from '@/models/radiators/Radiator';
import { computed, defineComponent, ref } from 'vue';
import RadiatorCardDeviceList from '@/components/Forms/RadiatorListFormComponents/RadiatorCardDeviceList.vue';
import { useRadiatorStore } from '@/store/entities/radiatorStore';
import { useDeviceStore } from '@/store/entities/deviceStore';
import { useEntitiesStore } from '@/store/entities/entitiesStore';
import { useFormStore } from '@/store/form/formStore';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import OptionsDropdown from '@/components/UI/Dropdown/OptionsDropdown.vue';
import { useImagesStore } from '@/store/images/imageStore';
import { useOrderStore } from '@/store/order/orderStore';
import { EntityTransactionType } from '@/models/OrderChanges';
import { useNotificationStore } from '@/store/notifications/notificationStore';
import { Notification } from '@/models/Notification';

export default defineComponent({
  name: 'RadiatorCard',
  components: {
    OptionsDropdown,
    BasePrompt,
    RadiatorCardDeviceList,
  },
  props: {
    radiator: {
      type: Radiator,
      required: true,
    },
    parentId: {
      type: String,
      required: true,
    },
    isSwapModeActive: {
      type: Boolean,
      required: false,
    },
  },
  setup(props) {
    const radiatorStore = useRadiatorStore();
    const deviceStore = useDeviceStore();
    const entitiesStore = useEntitiesStore();
    const formStore = useFormStore();
    const imagesStore = useImagesStore();

    const isDeleteActive = ref(false);
    const toBeDeletedEntity = ref<Radiator | undefined>(undefined);

    const isLastRadiator = computed(() => {
      if (props.radiator.parentRoomId) {
        const radiators = radiatorStore.getAllRadiatorsInRoom(
          props.radiator.parentRoomId
        );

        return !radiators.some(
          (r: Radiator) => r.ordinal > props.radiator.ordinal
        );
      }
      return true;
    });

    const isIdentified = computed(() => {
      return !!props.radiator.identificationResult;
    });

    const showRadiatorHasImage = computed(() => {
      return imagesStore.objectHasImages(props.radiator.id);
    });

    const goToEditRadiator = (radiator: Radiator) => {
      formStore.editEntity(radiator, props.parentId, {}, true);
    };

    const swapRadiator = (radiator: Radiator) => {
      radiatorStore.swapRadiator(radiator);
    };

    const getHKVsOfRadiator = computed(() => {
      return deviceStore.getDevicesOnRadiator(
        props.radiator.id,
        deviceTypes.HeatCostAllocator
      );
    });

    const getSRTsOfRadiator = computed(() => {
      return deviceStore.getDevicesOnRadiator(
        props.radiator.id,
        deviceTypes.SmartRadiatorThermostat
      );
    });

    const showEdit = computed(() => {
      return useOrderStore().hasTransaction(
        props.radiator?.id,
        EntityTransactionType.CREATE_ENTITY
      );
    });

    const showDelete = computed(() => {
      const hasNoDevices =
        getHKVsOfRadiator.value.length === 0 &&
        getSRTsOfRadiator.value.length === 0;
      return showEdit.value && hasNoDevices;
    });

    // Kept for backwards compatibility with existing template logic
    const isRadiatorDeletable = computed(() => {
      return (
        getHKVsOfRadiator.value.length === 0 &&
        getSRTsOfRadiator.value.length === 0
      );
    });

    const showDisabledNotification = () => {
      useNotificationStore().addNotification(
        new Notification(
          'Dieses Element wurde in einem früheren Auftrag erstellt und kann hier nicht bearbeitet werden.',
          '',
          'info'
        )
      );
    };

    const deleteEntityModal = (entity: Radiator) => {
      toBeDeletedEntity.value = entity;
      isDeleteActive.value = true;
    };

    const deleteRadiator = () => {
      if (toBeDeletedEntity.value !== undefined) {
        entitiesStore.deleteEntity(toBeDeletedEntity.value);
      }
      closeModal();
    };
    const closeModal = () => {
      isDeleteActive.value = false;
      toBeDeletedEntity.value = undefined;
    };

    return {
      goToEditRadiator,
      isIdentified,
      getSRTsOfRadiator,
      deleteEntityModal,
      showRadiatorHasImage,
      getHKVsOfRadiator,
      deleteRadiator,
      closeModal,
      swapRadiator,
      isLastRadiator,
      isDeleteActive,
      deviceTypes,
      deviceTypeDetails,
      isRadiatorDeletable,
      showEdit,
      showDelete,
      showDisabledNotification,
    };
  },
  computed: {
    DeviceTypeEnum() {
      return DeviceTypeEnum;
    },
  },
});
</script>

<style lang="scss" scoped>
.radiator-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 15px;
  box-shadow: inherit;
  padding: 0;
}

.radiator-card-header {
  border-radius: 15px;
  padding: 1.25rem;
}

.radiator-card-body {
  height: 100%;
  border-radius: 15px;
  padding: 1.25rem;
}

.hca-box .column {
  padding-left: 0;
}

.description {
  max-width: 800px;
}

.swap-btn {
  display: flex;
  justify-items: center;
  align-items: center;
  margin-left: 25px;
  font-size: 35px;
}
</style>
